import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, X } from "lucide-react";
import { HolidayIcon } from "./HolidayDecor";
import { activeHoliday } from "../../lib/holidays";

const SESSION_KEY = "pc-exit-popup-shown";
const MIN_TIME_ON_PAGE = 1500;

export const EXIT_POPUP_DEFAULTS = {
  exitPopupTitle: "Wait — before you go!",
  exitPopupText: "Your pup's next spa day is just one tap away. Request a time online or give us a call and we'll fit you in.",
  exitPopupButton: "Book a spa day",
};

export default function ExitIntentPopup({ settings }) {
  const [open, setOpen] = useState(false);
  const enabled = settings.exitPopupEnabled !== false;
  const holiday = activeHoliday(settings);

  useEffect(() => {
    if (!enabled || sessionStorage.getItem(SESSION_KEY)) return undefined;
    const start = Date.now();
    let lastY = window.scrollY;
    let lastT = Date.now();
    const show = () => {
      if (Date.now() - start < MIN_TIME_ON_PAGE || sessionStorage.getItem(SESSION_KEY)) return;
      sessionStorage.setItem(SESSION_KEY, "1");
      setOpen(true);
    };
    const onMouseOut = (e) => { if (!e.relatedTarget && e.clientY <= 0) show(); };
    const onScroll = () => {
      const y = window.scrollY;
      const now = Date.now();
      const depth = (y + window.innerHeight) / document.documentElement.scrollHeight;
      if (lastY - y > 350 && now - lastT < 700 && depth < 0.9 && lastY > window.innerHeight * 0.6) show();
      lastY = y;
      lastT = now;
    };
    document.addEventListener("mouseout", onMouseOut);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", onScroll);
    };
  }, [enabled]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;
  const title = settings.exitPopupTitle || EXIT_POPUP_DEFAULTS.exitPopupTitle;
  const text = settings.exitPopupText || EXIT_POPUP_DEFAULTS.exitPopupText;
  const button = settings.exitPopupButton || EXIT_POPUP_DEFAULTS.exitPopupButton;
  const tel = `tel:+1${(settings.phone || "").replace(/\D/g, "")}`;
  const bookTo = "/#book";
  const accent = holiday?.accent || "#6D28D9";

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-stone-950/55 p-4 backdrop-blur-sm sm:items-center"
      data-testid="exit-popup-overlay"
      onClick={() => setOpen(false)}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="exit-popup-title"
        data-testid="exit-popup"
        onClick={(e) => e.stopPropagation()}
        className="exit-popup-in relative w-full max-w-md overflow-hidden rounded-2xl border border-white/40 bg-white shadow-2xl"
      >
        <div className="h-2 w-full" style={{ background: holiday ? `linear-gradient(90deg, ${holiday.colors[0]}, ${holiday.colors[1]})` : "linear-gradient(90deg, #7C3AED, #C4B5FD)" }} />
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close"
          data-testid="exit-popup-close"
          className="absolute right-3 top-4 grid h-9 w-9 place-items-center rounded-full text-stone-500 transition-colors hover:bg-stone-100 hover:text-stone-900"
        >
          <X size={18} />
        </button>
        <div className="px-7 pb-7 pt-6 sm:px-9 sm:pb-9">
          <div className="mb-4 flex items-center gap-2">
            {holiday ? <HolidayIcon name={holiday.icons[0]} color={accent} size={28} className="holiday-bob" /> : <span className="h-7 w-7 rounded-full bg-lavender-100 ring-4 ring-lavender-50" />}
            <span className="text-xs font-semibold uppercase tracking-[0.3em]" style={{ color: accent }}>Pawtastic Cuts</span>
          </div>
          <h2 id="exit-popup-title" className="font-serif text-3xl leading-tight text-stone-900 sm:text-4xl" data-testid="exit-popup-title">{title}</h2>
          <p className="mt-3 text-base leading-relaxed text-stone-600" data-testid="exit-popup-text">{text}</p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Link to={bookTo} onClick={() => setOpen(false)} data-testid="exit-popup-book-button" className="btn-primary flex-1 px-6 py-3 text-center text-sm font-semibold">
              {button}
            </Link>
            {settings.phone && (
              <a href={tel} data-testid="exit-popup-call-button" className="btn-secondary flex flex-1 items-center justify-center gap-2 px-6 py-3 text-sm font-semibold">
                <Phone size={16} /> {settings.phone}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
